// Live PTZ 方向盤 + Zoom（APP#11, spec 21 §3.8）。
// 僅當 device capability_effective 含 PTZ cluster（0x8060）才渲染（自取 capability，
// 失敗即靜默隱藏——唯讀增益不阻斷 Live 頁）。
//
// 行為（spec 21 §3.8，Live 域）：
//   - 8 向方向盤:pointerdown → MoveRelative("pan,tilt" 相對角度);
//     pointerup/leave/cancel → Stop（無確認）。
//   - Zoom ＋/−:op:set Zoom,值型別依 report 自適應（int 數值 / enum 段選）。
//   - 「移動中/追蹤中」badge:聽 signaling room event（Move/Track 事件）。
// 信令未開（ready=false）時整列 disable；不等串流（2026-09-08，與 LiveControlBar 同規則）。

import { UiIcon } from '../../components/UiIcon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signaling } from '../../lib/ws';
import type { SignalFrame } from '../../lib/ws';
import { apiFetch } from '../../lib/api';
import { opCmd, opSet } from './settings/ops';
import { DEVICE_INCLUDE } from './useDeviceList';
import { canControlDevice, type DeviceSummary } from './deviceModel';
import {
  directionToPanTilt,
  hasPtzCluster,
  nextFreePresetSlot,
  nextZoomInt,
  parsePresets,
  presetStorageKey,
  ptzActivityOf,
  PTZ_CLEAR_PRESET,
  PTZ_GOTO_PRESET,
  PTZ_LIST_PRESETS,
  PTZ_MOVE_RELATIVE,
  PTZ_SET_PRESET,
  PTZ_STOP,
  PTZ_ZOOM,
  ptzPanOnly,
  removePreset,
  upsertPreset,
  zoomKind,
  type PtzActivity,
  type PtzDirection,
  type PtzPreset,
} from './ptz';

/** 裝置 NAK「不支援此命令」（agent 不認得 id 回 unknown_cmd，或 profile 減項回 unsupported_command）。 */
export function isUnsupportedCmd(err: unknown): boolean {
  const code = (err as { frame?: { code?: unknown } } | null)?.frame?.code;
  if (code === 'unsupported_command' || code === 'unknown_cmd') return true;
  const m = err instanceof Error ? err.message : String(err);
  return m.includes('unsupported_command') || m.includes('unknown_cmd');
}
import './ptz.css';

// 方向盤格位 → 3×3 grid（中央為 STOP/狀態,非方向）。
const GRID: Array<PtzDirection | null> = ['up-left', 'up', 'up-right', 'left', null, 'right', 'down-left', 'down', 'down-right'];
// pan-only 機種（465V2）只剩中間那一列 ← ● →（ptz.ts ptzPanOnly 註解）。
const GRID_PAN_ONLY: Array<PtzDirection | null> = ['left', null, 'right'];

const ARROW: Record<PtzDirection, string> = {
  'up-left': '↖',
  up: '↑',
  'up-right': '↗',
  left: '←',
  right: '→',
  'down-left': '↙',
  down: '↓',
  'down-right': '↘',
};

/** capability 自取結果:undefined=未知（載入中,不渲染）/ true / false。 */
function usePtzCapability(deviceId: string): { has: boolean | undefined; panOnly: boolean } {
  const [has, setHas] = useState<boolean | undefined>(undefined);
  const [panOnly, setPanOnly] = useState(false);
  useEffect(() => {
    let alive = true;
    void apiFetch<DeviceSummary>(`/v3/devices/${encodeURIComponent(deviceId)}`, { query: { include: DEVICE_INCLUDE } })
      .then((res) => {
        if (!alive) return;
        const clusters = (res.data?.capability_effective ?? []).map((c) => c.cluster ?? '');
        // 21 §2.1（APP#137）：無 control 權限 → 整盤隱藏（PTZ op 需 command:control + `control`）。
        setHas(hasPtzCluster(clusters) && !!res.data && canControlDevice(res.data));
        setPanOnly(ptzPanOnly(res.data?.model));
      })
      .catch(() => {
        if (alive) setHas(false); // 取不到 → 靜默隱藏
      });
    return () => {
      alive = false;
    };
  }, [deviceId]);
  return { has, panOnly };
}

export function PtzPad({ deviceId, ready }: { deviceId: string; ready: boolean }) {
  const { t } = useTranslation();
  const { has: hasPtz, panOnly } = usePtzCapability(deviceId);
  const [activity, setActivity] = useState<PtzActivity>('idle');
  // Zoom 值型別自適應:首個 report/op:set 回值決定 int vs enum;預設 int 倍率。
  const [zoomValue, setZoomValue] = useState<number | string | null>(null);
  const movingRef = useRef(false);

  // 預置點（APP#47；業主 2026-09-10 裁決 D'：新增搬到這裡，設定頁 PtzSection 只留改名／刪除）。
  // 裝置端只存 pose（SetPreset/GotoPreset/ClearPreset），名稱 client-local（localStorage，與設定頁同 key）。
  const [presets, setPresets] = useState<PtzPreset[]>([]);
  /** null=未探測 / false=裝置對 preset 命令 NAK unsupported_command → 整列隱藏（common/04 §4.11）。 */
  const [presetsSupported, setPresetsSupported] = useState<boolean | null>(null);
  const [presetBusy, setPresetBusy] = useState(false);
  const [presetError, setPresetError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ mode: 'add' } | { mode: 'rename'; slot: number } | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [menuSlot, setMenuSlot] = useState<number | null>(null);
  const probedRef = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    try {
      setPresets(parsePresets(localStorage.getItem(presetStorageKey(deviceId))));
    } catch {
      setPresets([]);
    }
  }, [deviceId]);
  // 支援探測：信令開了先送一次 ListPresets，明確 NAK 不支援才隱藏；其他結果照常顯示。
  // （465V2 2026-09-10 實機：ListPresets 回 report、SetPreset 卻回 unknown_cmd —— 真正不支援由 presetCmd 補判。）
  useEffect(() => {
    if (hasPtz !== true || !ready || probedRef.current) return;
    probedRef.current = true;
    opCmd(deviceId, PTZ_LIST_PRESETS)
      .then(() => {
        if (alive.current) setPresetsSupported(true);
      })
      .catch((err: unknown) => {
        if (alive.current && isUnsupportedCmd(err)) setPresetsSupported(false);
      });
  }, [hasPtz, ready, deviceId]);

  // room 事件 → 移動中/追蹤中 badge（spec 21 §3.8）。
  useEffect(() => {
    if (hasPtz !== true) return;
    const off = signaling.on('event', (frame: SignalFrame) => {
      if (frame.device_id !== deviceId) return;
      const eventId = (frame.id ?? frame.event_id ?? (frame.payload as { id?: string; event_id?: string } | undefined)?.id ?? (frame.payload as { event_id?: string } | undefined)?.event_id) as
        | string
        | undefined;
      const act = ptzActivityOf(eventId);
      if (act) setActivity(act);
    });
    return off;
  }, [hasPtz, deviceId]);

  const sendStop = useCallback(() => {
    if (!movingRef.current) return;
    movingRef.current = false;
    void opCmd(deviceId, PTZ_STOP).catch(() => {});
  }, [deviceId]);

  const onDirDown = useCallback(
    (dir: PtzDirection) => {
      if (!ready) return;
      movingRef.current = true;
      void opCmd(deviceId, PTZ_MOVE_RELATIVE, directionToPanTilt(dir)).catch(() => {});
    },
    [deviceId, ready]
  );

  const onZoom = useCallback(
    (delta: number) => {
      if (!ready) return;
      const kind = zoomKind(zoomValue);
      if (kind === 'enum') {
        // enum Zoom:在已知段位間移動（值取自 report;此處以 0/1 近/遠示意）。
        const cur = typeof zoomValue === 'number' ? zoomValue : 0;
        const next = delta > 0 ? 1 : 0;
        if (next === cur) return;
        setZoomValue(next);
        void opSet(deviceId, PTZ_ZOOM, next).catch(() => {});
        return;
      }
      // int（含未知預設）:倍率 stepper。
      const cur = typeof zoomValue === 'number' ? zoomValue : 1;
      const next = nextZoomInt(cur, delta);
      if (next === cur) return;
      setZoomValue(next);
      void opSet(deviceId, PTZ_ZOOM, next).catch(() => {});
    },
    [deviceId, ready, zoomValue]
  );

  // 卸載/離開串流確保補送 Stop（避免裝置卡在持續轉動）。
  useEffect(() => {
    if (!ready) sendStop();
  }, [ready, sendStop]);
  useEffect(() => () => sendStop(), [sendStop]);

  if (hasPtz !== true) return null;

  function persistPresets(next: PtzPreset[]) {
    setPresets(next);
    try {
      localStorage.setItem(presetStorageKey(deviceId), JSON.stringify(next));
    } catch {
      /* localStorage 不可用（隱私模式）→ 僅記憶體 */
    }
  }
  async function presetCmd(id: string, slot: number): Promise<boolean> {
    setPresetBusy(true);
    setPresetError(null);
    try {
      await opCmd(deviceId, id, slot);
      return true;
    } catch (err) {
      if (!alive.current) return false;
      // 操作中被 NAK 不隱藏整列（隱藏會連錯誤字一起消失）；隱藏只由 ListPresets 探測決定。
      if (isUnsupportedCmd(err)) {
        setPresetError(t('devices.ptzPresetUnsupported'));
      } else {
        setPresetError(err instanceof Error ? err.message : t('deviceSettings.setFailed'));
      }
      return false;
    } finally {
      if (alive.current) setPresetBusy(false);
    }
  }
  async function submitName() {
    if (!editing) return;
    const name = nameDraft.trim();
    if (name.length < 1 || name.length > 24) {
      setPresetError(t('deviceSettings.ptzPresetNameError'));
      return;
    }
    if (editing.mode === 'rename') {
      persistPresets(upsertPreset(presets, editing.slot, name));
      setEditing(null);
      setNameDraft('');
      return;
    }
    const slot = nextFreePresetSlot(presets);
    if (slot === null) {
      setPresetError(t('deviceSettings.ptzPresetFull'));
      return;
    }
    if (await presetCmd(PTZ_SET_PRESET, slot)) {
      persistPresets(upsertPreset(presets, slot, name));
      setEditing(null);
      setNameDraft('');
    }
  }
  async function deletePreset(slot: number) {
    if (await presetCmd(PTZ_CLEAR_PRESET, slot)) persistPresets(removePreset(presets, slot));
  }
  const nextSlot = nextFreePresetSlot(presets);

  const zoomLabel = typeof zoomValue === 'number' ? `${zoomValue}×` : typeof zoomValue === 'string' ? zoomValue : '—';

  return (
    <div className="ptz-pad" role="group" aria-label={t('devices.ptzGroupLabel')} data-testid="ptz-pad">
      <div className="ptz-wheel">
        {(panOnly ? GRID_PAN_ONLY : GRID).map((dir, i) =>
          dir ? (
            <button
              key={dir}
              className="ptz-dir"
              data-testid={`ptz-${dir}`}
              disabled={!ready}
              aria-label={t(`devices.ptzMove_${dir}`)}
              onPointerDown={(e) => {
                e.preventDefault();
                e.currentTarget.setPointerCapture?.(e.pointerId);
                onDirDown(dir);
              }}
              onPointerUp={sendStop}
              onPointerLeave={sendStop}
              onPointerCancel={sendStop}
            >
              <span aria-hidden="true">{ARROW[dir]}</span>
            </button>
          ) : (
            <span key={`center-${i}`} className="ptz-center" aria-hidden="true">
              ●
            </span>
          )
        )}
      </div>

      <div className="ptz-zoom" role="group" aria-label={t('devices.ptzZoomGroupLabel')}>
        <button className="btn-ghost lv-zoom-btn" data-testid="ptz-zoom-out" disabled={!ready} aria-label={t('devices.zoomOut')} onClick={() => onZoom(-1)}>
          −
        </button>
        <span className="ptz-zoom-level" data-testid="ptz-zoom-level" aria-live="polite">
          {zoomLabel}
        </span>
        <button className="btn-ghost lv-zoom-btn" data-testid="ptz-zoom-in" disabled={!ready} aria-label={t('devices.zoomIn')} onClick={() => onZoom(1)}>
          ＋
        </button>
      </div>

      {activity !== 'idle' && (
        <span className="ptz-badge" role="status" data-testid="ptz-badge">
          <UiIcon name={activity === 'tracking' ? 'target' : 'compass'} />
          {activity === 'tracking' ? t('devices.ptzTracking') : t('devices.ptzMoving')}
        </span>
      )}

      {/* 預置點列：「＋ 新增儲存點」固定最前（放最後會被擠出視線）；chip 點一下＝前往、⋯＝前往／重新命名／刪除。
          新增在鏡頭移動中停用（轉到一半存位置沒有意義）。 */}
      {presetsSupported !== false && (
        <div className="ptz-presets" data-testid="ptz-presets">
          <div className="ptz-presets-head">
            <span>{t('deviceSettings.ptzPresets')}</span>
            {presets.length > 0 && <span className="ptz-presets-hint">{t('devices.ptzPresetTapHint')}</span>}
          </div>
          <div className="ptz-chips">
            <span className="ptz-chip add">
              <button
                type="button"
                data-testid="ptz-preset-add"
                disabled={!ready || presetBusy || activity !== 'idle'}
                onClick={() => {
                  setPresetError(null);
                  setNameDraft('');
                  setMenuSlot(null);
                  setEditing({ mode: 'add' });
                }}
              >
                <span aria-hidden="true">＋</span>
                {t('devices.ptzPresetAddPoint')}
              </button>
            </span>
            {presets.map((p) => (
              <span key={p.slot} className="ptz-chip-wrap">
                <span className="ptz-chip">
                  <button type="button" data-testid={`ptz-preset-${p.slot}`} disabled={!ready || presetBusy} onClick={() => void presetCmd(PTZ_GOTO_PRESET, p.slot)}>
                    <UiIcon name="pin" />
                    {p.name}
                  </button>
                  <button
                    type="button"
                    className="ptz-chip-more"
                    aria-label={t('devices.ptzPresetMore', { name: p.name })}
                    aria-haspopup="menu"
                    aria-expanded={menuSlot === p.slot}
                    disabled={presetBusy}
                    onClick={() => setMenuSlot(menuSlot === p.slot ? null : p.slot)}
                  >
                    ⋯
                  </button>
                </span>
                {menuSlot === p.slot && (
                  <div className="ptz-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      disabled={!ready}
                      onClick={() => {
                        setMenuSlot(null);
                        void presetCmd(PTZ_GOTO_PRESET, p.slot);
                      }}
                    >
                      {t('devices.ptzPresetGoto')}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuSlot(null);
                        setPresetError(null);
                        setNameDraft(p.name);
                        setEditing({ mode: 'rename', slot: p.slot });
                      }}
                    >
                      {t('devices.ptzPresetRename')}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="danger"
                      disabled={!ready}
                      onClick={() => {
                        setMenuSlot(null);
                        void deletePreset(p.slot);
                      }}
                    >
                      {t('deviceSettings.ptzPresetDelete')}
                    </button>
                  </div>
                )}
              </span>
            ))}
          </div>
          {presets.length === 0 && !editing && <span className="ptz-presets-hint">{t('devices.ptzPresetEmptyHint')}</span>}
          {editing && (
            <form
              className="ptz-preset-form"
              onSubmit={(e) => {
                e.preventDefault();
                void submitName();
              }}
            >
              {editing.mode === 'add' && (
                <span className="ptz-presets-hint" style={{ flexBasis: '100%' }}>
                  {t('devices.ptzPresetAddBody')}
                  {nextSlot !== null ? ` ${t('devices.ptzPresetSlotHint', { n: nextSlot })}` : ''}
                </span>
              )}
              <input
                autoFocus
                value={nameDraft}
                maxLength={24}
                placeholder={t('deviceSettings.ptzPresetNamePlaceholder')}
                aria-label={t('deviceSettings.ptzPresetName')}
                data-testid="ptz-preset-name"
                onChange={(e) => setNameDraft(e.target.value)}
              />
              <button type="submit" className="btn-primary" data-testid="ptz-preset-save" disabled={presetBusy || nameDraft.trim().length === 0}>
                {presetBusy ? t('common.saving') : t('common.save')}
              </button>
              <button
                type="button"
                className="btn-ghost"
                disabled={presetBusy}
                onClick={() => {
                  setEditing(null);
                  setNameDraft('');
                }}
              >
                {t('common.cancel')}
              </button>
            </form>
          )}
          {presetError && (
            <p className="ptz-preset-error" role="alert">
              {presetError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
